import { getPedidos, deletePedido } from "@spark/services/pedidos";
import { useState, useEffect, useCallback, useRef } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
export const usePedidosListagem = (cargaId = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(5);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pedidoToDelete, setPedidoToDelete] = useState(null);
  const isMountedRef = useRef(true);
  const [filtros, setFiltros] = useState({
    NumeroDoPedido: '',
    NumeroCarga: '',
    NumeroCTE: '',
    NumeroNF: '',
    LocalPalletizacao: '',
    DataAgendamentoInicio: '',
    DataAgendamentoFim: '',
    DataIntegracaoInicio: '',
    DataIntegracaoFim: '',
    CargaId: cargaId || ''
  }); // Cleanup quando componente é desmontado

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []); // Carregar dados

  const fetchData = useCallback(async (filtrosParaBuscar = filtros) => {
    if (!isMountedRef.current) return;

    try {
      console.log('🌐 Buscando pedidos com filtros:', filtrosParaBuscar);
      setLoading(true); // Preparar filtros para o serviço

      const filters = {
        quantidadePorPagina,
        pagina: paginaAtual,
        ...(filtrosParaBuscar.NumeroDoPedido && {
          NumeroDoPedido: filtrosParaBuscar.NumeroDoPedido
        }),
        ...(filtrosParaBuscar.NumeroCarga && {
          NumeroCarga: filtrosParaBuscar.NumeroCarga
        }),
        ...(filtrosParaBuscar.NumeroCTE && {
          NumeroCTE: filtrosParaBuscar.NumeroCTE
        }),
        ...(filtrosParaBuscar.NumeroNF && {
          NumeroNF: filtrosParaBuscar.NumeroNF
        }),
        ...(filtrosParaBuscar.LocalPalletizacao && {
          LocalPalletizacao: filtrosParaBuscar.LocalPalletizacao
        }),
        ...(filtrosParaBuscar.DataAgendamentoInicio && {
          DataAgendamentoInicio: filtrosParaBuscar.DataAgendamentoInicio
        }),
        ...(filtrosParaBuscar.DataAgendamentoFim && {
          DataAgendamentoFim: filtrosParaBuscar.DataAgendamentoFim
        }),
        ...(filtrosParaBuscar.DataIntegracaoInicio && {
          DataIntegracaoInicio: filtrosParaBuscar.DataIntegracaoInicio
        }),
        ...(filtrosParaBuscar.DataIntegracaoFim && {
          DataIntegracaoFim: filtrosParaBuscar.DataIntegracaoFim
        }),
        ...(filtrosParaBuscar.CargaId && {
          CargaId: filtrosParaBuscar.CargaId
        })
      };
      const responseData = await getPedidos(filters);
      if (!isMountedRef.current) return;
      console.log('📥 Resposta da API:', responseData);
      console.log('📦 Registros:', responseData.registros);
      console.log('📦 Total de registros:', responseData.totalRegistros);
      const dadosProcessados = (responseData.registros || []).map(item => ({
        id: item.Id,
        numeroDoPedido: item.NumeroDoPedido || '',
        numeroDaCarga: item.NumeroDaCarga || '',
        tipoDaOperacao: item.TipoDaOperacao || '',
        situacaoCarga: item.SituacaoCarga || '',
        origem: item.Origem || '',
        destino: item.Destino || '',
        localPalletizacao: item.LocalPalletizacao || '',
        dataAgendamento: item.DataAgendamento ? new Date(item.DataAgendamento).toLocaleDateString('pt-BR') : '',
        dataIntegracao: item.DataIntegracao ? new Date(item.DataIntegracao).toLocaleDateString('pt-BR') : '',
        ctes: item.ctes || '',
        notasFiscais: item.notasFiscais || '',
        // Campos adicionais da API
        idCarga: item.IdCarga
      }));
      setData(dadosProcessados);
      setTotalRegistros(responseData.totalRegistros || 0);
    } catch (error) {
      console.error('❌ Erro ao buscar pedidos:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [filtros, paginaAtual, quantidadePorPagina]); // Carregar dados iniciais

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Aplicar filtros

  const handleFilterApply = useCallback(newFilters => {
    console.log('🔍 Aplicando filtros:', newFilters);
    setFiltros(newFilters);
    setPaginaAtual(1); // Reset para primeira página

    setFilterLoading(true);
    fetchData(newFilters).finally(() => {
      if (isMountedRef.current) {
        setFilterLoading(false);
      }
    });
  }, [fetchData]); // Limpar filtros

  const handleLimparFiltros = useCallback(clearedFilters => {
    console.log('🧹 Limpando filtros');
    setFiltros(clearedFilters);
    setPaginaAtual(1); // Reset para primeira página

    setFilterLoading(true);
    fetchData(clearedFilters).finally(() => {
      if (isMountedRef.current) {
        setFilterLoading(false);
      }
    });
  }, [fetchData]); // Excluir pedido

  const handleExcluir = useCallback(id => {
    console.log('🗑️ Solicitando exclusão do pedido:', id);
    const pedido = data.find(item => item.id === id);
    setPedidoToDelete({
      id,
      numeroDoPedido: pedido?.numeroDoPedido || id
    });
    setShowDeleteModal(true);
  }, [data]); // Confirmar exclusão

  const confirmDelete = useCallback(async () => {
    if (!pedidoToDelete) return;

    try {
      console.log('🗑️ Confirmando exclusão do pedido:', pedidoToDelete.id);
      await deletePedido(pedidoToDelete.id);
      notification.success('Pedido excluído com sucesso!');
      setShowDeleteModal(false);
      setPedidoToDelete(null);
      fetchData(); // Recarregar dados
    } catch (error) {
      console.error('❌ Erro ao excluir pedido:', error); // Erro tratado automaticamente pelo interceptor global do axios
    }
  }, [pedidoToDelete, fetchData]); // Cancelar exclusão

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setPedidoToDelete(null);
  }, []);
  return {
    data,
    loading,
    filterLoading,
    totalRegistros,
    paginaAtual,
    quantidadePorPagina,
    showDeleteModal,
    pedidoToDelete,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    confirmDelete,
    cancelDelete,
    setPaginaAtual,
    setQuantidadePorPagina
  };
};
